import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase, initialAuthType } from "@/lib/supabase";
import { Brand } from "@/components/Brand";
import { PasswordInput } from "@/components/PasswordInput";

export function Welcome() {
  const { session, employee, loading, refreshEmployee } = useAuth();
  const nav = useNavigate();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive this SYNCHRONOUSLY during render — never from a useEffect. The invite
  // function pre-creates the hq_employees row, so `employee` is truthy the instant
  // the invite session resolves. If needsPassword were effect-set state (false on
  // first render), the `employee && !needsPassword` guard below would fire on that
  // first render and redirect the invitee straight past this form into onboarding,
  // never letting them set a password. Computing it inline closes that race.
  //
  // Force password setup when this is an invite/recovery landing (initialAuthType
  // is captured at module load — reading the hash here is unreliable because
  // supabase-js has usually already consumed it). Belt and braces: an invited user
  // is signed in by the link itself, so last_sign_in_at tells us nothing; what does
  // is the password_set flag — anyone here without it has no password of their own.
  const meta = session?.user?.user_metadata as { password_set?: boolean } | undefined;
  const needsPassword = !!session?.user && (
    initialAuthType === "invite" || initialAuthType === "recovery" || initialAuthType === "signup"
    || meta?.password_set !== true
  );

  useEffect(() => {
    // Pre-fill name from invite metadata.
    const m = (session?.user?.user_metadata as { first_name?: string; last_name?: string } | undefined);
    if (m?.first_name) setFirst(m.first_name);
    if (m?.last_name) setLast(m.last_name);
  }, [session]);

  // Auth is still resolving (e.g. invite link is still being parsed from the URL
  // hash). Don't redirect anywhere yet — that race was kicking invitees out to
  // /login before their session got established, breaking the whole invite
  // flow.
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-gray2">Loading…</div>;
  }
  if (!session) return <Navigate to="/login" replace />;
  // If they're already set up AND don't need password, route them through.
  if (employee && !needsPassword) return <Navigate to="/" replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (needsPassword) {
      if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
      if (password !== confirm) { setError("Passwords don't match."); return; }
    }

    setBusy(true);
    try {
      // Set password if this is an invite flow. The password_set flag is what
      // stops us prompting again on a later visit — see the effect above.
      if (needsPassword) {
        const { error: pwErr } = await supabase.auth.updateUser({ password, data: { password_set: true } });
        if (pwErr) throw new Error(pwErr.message);
      }
      // Ensure employee row exists. The invite function pre-creates it, but a self-signup user
      // lands here without one, so we call bootstrap (idempotent).
      if (!employee) {
        const { error: bErr } = await supabase.rpc("hq_bootstrap_employee", {
          p_email: session?.user?.email,
          p_first_name: first,
          p_last_name: last,
        });
        if (bErr) throw new Error(bErr.message);
      }
      await refreshEmployee();
      nav("/", { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-offwhite p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Brand /></div>
        <div className="card shadow-soft">
          <h1 className="mb-1 text-2xl font-bold tracking-tight">Welcome to Scenta HQ</h1>
          <p className="mb-6 text-sm text-gray2">
            {needsPassword ? "Set a password to finish activating your account." : "One more step — tell us your name so we can finish setting up your profile."}
          </p>
          {error && <div className="mb-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
          <form onSubmit={onSubmit} className="space-y-4">
            {!employee && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">First name</label>
                  <input className="input" required value={first} onChange={e => setFirst(e.target.value)} autoFocus />
                </div>
                <div>
                  <label className="label">Last name</label>
                  <input className="input" required value={last} onChange={e => setLast(e.target.value)} />
                </div>
              </div>
            )}
            {needsPassword && (
              <>
                <div>
                  <label className="label">Choose a password</label>
                  <PasswordInput required minLength={6} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" autoFocus={!!employee} />
                  <p className="mt-1 text-xs text-gray2">At least 6 characters.</p>
                </div>
                <div>
                  <label className="label">Confirm password</label>
                  <PasswordInput required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
                </div>
              </>
            )}
            <button className="btn w-full btn-lg" disabled={busy}>{busy ? "Setting up…" : "Continue"}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
